import * as React from 'react';
import IMask from 'imask';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { Text as Interface } from '../../design/components';

export const Text: React.FC<Interface> = ({
  name, description, state, placeholder, hidden, inline, readonly, disabled, required,
  error, type = 'text', multiline, maxlength, minlength, pattern, autocomplete, mask
}) => {
  const [value, setValue] = state || ['', () => {}];

  const masked = React.useMemo(() => {
    if (mask && !(multiline && multiline > 1)) {
      return IMask.createMask({ mask });
    }
    return null;
  }, [mask, multiline]);

  // Inline mode: render just the text value
  if (inline) {
    return (
      <span className={cn(hidden && 'hidden')}>
        {value || ''}
      </span>
    );
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.target.value;
    if (masked) {
      masked.resolve(input);
      setValue(masked.unmaskedValue);
    } else {
      setValue(input);
    }
  };

  const getDisplayValue = () => {
    if (masked && value) {
      masked.resolve(value);
      return masked.value;
    }
    return value;
  };

  const isTextarea = multiline && multiline > 1;

  return (
    <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
      {name && (
        <Label htmlFor={name} className={cn(error && 'text-destructive')}>
          {name}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {isTextarea ? (
        <Textarea
          id={name}
          name={name}
          value={getDisplayValue()}
          placeholder={placeholder || name}
          onChange={onChange}
          disabled={disabled}
          readOnly={readonly}
          required={required}
          rows={multiline}
          maxLength={maxlength}
          minLength={minlength}
          autoComplete={autocomplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || description ? `${name}-description` : undefined}
          className={cn(readonly && 'bg-muted cursor-default')}
        />
      ) : (
        <Input
          id={name}
          name={name}
          type={type}
          value={getDisplayValue()}
          placeholder={placeholder || name}
          onChange={onChange}
          disabled={disabled}
          readOnly={readonly}
          required={required}
          maxLength={maxlength}
          minLength={minlength}
          pattern={pattern}
          autoComplete={autocomplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || description ? `${name}-description` : undefined}
          className={cn(readonly && 'bg-muted cursor-default')}
        />
      )}

      {(error || description) && (
        <p
          id={`${name}-description`}
          className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}
        >
          {error || description}
        </p>
      )}
    </div>
  );
};
